import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { ClientContactNotificationConstant } from '@app/core/_constant/b2b-clients.constants';
import { ValidationConstants } from '@app/core/_constant/validation.constants';
import { ClientUsersService } from '@app/core/client/services/client-users.service';
import { CompanyService } from '@app/core/client-user';
import { ClientContactUtilsService } from '@app/core/client-user/services/client-contact-utils.service';

import { BusinessAccountContactsService } from './../../../core/client-user/services/business-account-contacts.service';
import { UserAccountsService } from './../../../core/client-user/services/user-accounts.service';

@Component({
  selector: 'cnc-popup-form',
  templateUrl: './popup-form.component.html',
  styleUrls: ['./popup-form.component.scss'],
})
export class PopupFormComponent implements OnInit, OnDestroy {
  @Input()
  clientUser :any;

  @Input()
  client :any;

  @Input()
  primaryContact :boolean;

  @Input()
  filter :any;

  @ViewChild('companyInput')
  companyInput :ElementRef<HTMLInputElement>;

  assignees :any[] = [];
  companies :any[] = [];
  filteredCompanies :any[] = [];
  formData :UntypedFormGroup;
  loading :boolean = false;
  pageTitle :string = 'Add New Client Contact';
  passMaxLength :number = ValidationConstants.password.max;
  passMinLength :number = ValidationConstants.password.min;
  selectedCompanies :any = [];
  separatorKeysCodes :number[] = [ENTER, COMMA];
  settings :any[] = [];
  submitted :boolean = false;

  companies$ :Observable<any[]>;
  companyLoading$ :Observable<boolean>;
  companyInput$ = new Subject<string>();

  private onDestroy$ = new Subject();

  clientContactNotificationConstant = ClientContactNotificationConstant;

  constructor(
    private fb :UntypedFormBuilder,
    private toastr :ToastrService,
    public activeModal :NgbActiveModal,
    public companyService :CompanyService,
    public clientUserService :ClientUsersService,
    public clientContactUtils :ClientContactUtilsService,
    private businessAccountContactsService :BusinessAccountContactsService,
    private userAccountsService :UserAccountsService,
    private snackBar :MatSnackBar,
    private translate :TranslateService,
  ) {}

  ngOnInit() {
    this.companyService.setCompanies([]);
    this.getDataFromService();
    this.onInputMeasureUpdated().subscribe();
    this.getCompanies().subscribe();
    this.initSettings(this.client);

    const settings = this.createSettingsControls(this.settings);

    this.formData = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      receivingBankName: ['', null],
      receivingBankAddress: ['', null],
      email: ['', [Validators.required, this.emailValidator()]],
      password: ['', null],
      salesRepAssigneeIds: [''],
      phoneOne: [''],
      phoneTwo: [''],
      settings,
    });

    if (this.clientUser) {
      this.formData.removeControl('settings');
    }

    if (this.clientUser && this.clientUser.id) {
      this.formData.patchValue({
        firstName: this.clientUser.firstName,
        lastName: this.clientUser.lastName,
        receivingBankName: this.clientUser.receivingBankName,
        receivingBankAddress: this.clientUser.receivingBankAddress,
        email: this.clientUser.email,
        phoneOne: this.clientUser.phoneOne,
        phoneTwo: this.clientUser.phoneTwo,
      });
      this.pageTitle = 'Update Client Contact';
      this.assignees = this.clientUser.salesRepAssignees.map(
        (assignee) => assignee.id,
      );
    }

    if (this.client && this.client.id) {
      this.formData.patchValue({
        business_account_id: this.client.id,
      });
    }

    this.listenAndUpdatePortalFieldsValidity();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  trackById(company :any) :number {
    return company?.id;
  }

  submit() :void {
    this.submitted = true;
    const blankBusinessAccount = this.formData.value.settings
      ? this.formData.value.settings
          .map((data :any) => !data.businessAccountId)
          .indexOf(true) !== -1
      : false;

    if (this.formData.invalid) {
      let swalText :string = '';
      if (blankBusinessAccount) {
        swalText = this.translate.instant(
          'MESSAGES.COMPANY.REQUIRED_FIELDS_MESSAGE',
        );
      } else {
        swalText = this.translate.instant('MESSAGES.COMPANY.COMPANY_EXIST_MESSAGE');
        this.formData.get('settings').setErrors(null);
      }
      Swal.fire({
        title: this.translate.instant('MESSAGES.COMPANY.TITLE'),
        text: swalText,
        type: 'error',
        confirmButtonText: 'Back to Form',
      });

      return;
    }
    const payload = {
      ...this.formData.value,
    };

    if (!this.clientUser) {
      payload.settings = this.formData.value.settings.map((setting :any) => ({
        ...setting,
        receiveQuotationPdf: setting.primaryContact
          ? true
          : setting?.receiveQuotationPdf,
        receivePurchaseOrderPdf: setting.primaryContact
          ? true
          : setting?.receivePurchaseOrderPdf,
        receivePurchaseOrderInvoicePdf: setting.primaryContact
          ? true
          : setting?.receivePurchaseOrderInvoicePdf,
        businessAccountId: setting.businessAccountId.id,
      }));
    } else {
      delete payload.password;
    }

    this.loading = true;

    this.clientUserService
      .checkEmailOfClientUsersIfExist(payload['email'])
      .subscribe((response) => {
        if (response.email && !this.clientUser) {
          Swal.fire({
            title: 'Form Error!',
            text: 'Email already in use.',
            type: 'error',
            confirmButtonText: 'Back to Form',
          });
          this.loading = false;

          return;
        } else if (response?.email && response?.email !== this.clientUser?.email) {
          Swal.fire({
            title: 'Form Error!',
            text: 'Email already in use.',
            type: 'error',
            confirmButtonText: 'Back to Form',
          });
          this.loading = false;

          return;
        } else if (!response?.email && this.clientUser?.email) {
          this.save(payload);
        } else {
          this.save(payload);
        }
      });
  }

  save(payload :any) :void {
    if (this.clientUser && this.clientUser.id) {
      this.userAccountsService
        .update({ id: this.clientUser.id, payload })
        .pipe(
          catchError((resp :any) => {
            this.snackBar.open(resp.error, 'Failed', {
              duration: 3 * 1000, // 3 sec
            });

            return throwError(resp.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe((resp) => {
          this.userAccountsService.get(this.filter);
          this.activeModal.close(resp);
          this.loading = false;
          this.snackBar.open('Contact successfully updated.', 'Success', {
            duration: 3 * 1000, // 3 sec
          });
        });
    } else {
      this.businessAccountContactsService
        .createBulk(payload)
        .pipe(
          catchError((response) => {
            this.loading = false;
            this.toastr.error(
              'Error creating client user. Please try again later or contact support.',
            );

            return throwError(response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe((resp) => {
          this.userAccountsService.get(this.filter);
          this.toastr.success('New client user created.');
          this.loading = false;
          this.activeModal.close(resp);
        });
    }
  }

  trackCompanyByFn(item :any) {
    return item.id;
  }

  noWhitespaceValidator() :ValidatorFn {
    return (control :AbstractControl) :{ [key :string] :boolean } | null => {
      const hasWhitespace = /\s/g.test(control.value);
      const isValid = !hasWhitespace;

      return isValid ? null : { whitespace: true };
    };
  }

  emailValidator() :ValidatorFn {
    return (control :AbstractControl) :{ [key :string] :boolean } | null => {
      if (!control.value) return null;
      const isValid =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          control.value,
        );

      return isValid ? null : { email: true };
    };
  }

  private businessAccountValidator() :ValidatorFn {
    return (control :AbstractControl) :{ [key :string] :boolean } | null => {
      const valid = typeof control.value !== 'string';

      return valid ? null : { businessAccountId: true };
    };
  }

  customSearchFn(term :string, item :object) :boolean {
    const termInput :string = term.toLocaleLowerCase();

    return (
      item['lastname'].toLocaleLowerCase().indexOf(termInput) > -1 ||
      item['firstname'].toLocaleLowerCase().indexOf(termInput) > -1
    );
  }

  onCompanyInputChange(event :Event) :void {
    this.companyInput$.next(event.target['value']);
  }

  displayFn(company :any) :string {
    return company && company.business_name ? company.business_name : '';
  }

  addCompany() :void {
    this.settingsControl.push(
      this.fb.group({
        businessAccountId: [
          null,
          [Validators.required, this.businessAccountValidator()],
        ],
        primaryContact: [false],
        receiveQuotationPdf: [false],
        receivePurchaseOrderPdf: [false],
        receivePurchaseOrderInvoicePdf: [false],
      }),
    );
  }

  removeCompany(index :number) :void {
    const control = this.settingsControl;

    const removedCompany = control.at(index).value;

    control.removeAt(index);
  }

  get busiessAccountsControl() :UntypedFormArray {
    return this.formData.controls.business_account_id as UntypedFormArray;
  }

  get settingsControl() :UntypedFormArray {
    return this.formData.controls.settings as UntypedFormArray;
  }

  private initSettings(client :any) :void {
    this.settings = [
      {
        businessAccountId: client ?? null,
        primaryContact: false,
        receiveQuotationPdf: false,
        receivePurchaseOrderPdf: false,
        receivePurchaseOrderInvoicePdf: false,
      },
    ];
  }

  private getDataFromService() :void {
    this.companies$ = this.companyService.companies$;
    this.companyLoading$ = this.companyService.loading$;
  }

  private getCompanies() :Observable<any> {
    return this.companies$.pipe(
      tap((companies) => {
        this.companies = companies;
        this.filteredCompanies = this.getFilteredCompanies({
          companies,
          selectedCompanies: this.selectedCompanies,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getFilteredCompanies(data :{
    companies :any[];
    selectedCompanies :any[];
  }) :any[] {
    const { companies, selectedCompanies } = data;

    return companies.filter((company) => {
      const index = selectedCompanies.findIndex((c) => c.id === company.id);

      return index === -1;
    });
  }

  private listenAndUpdatePortalFieldsValidity() :void {
    this.formData
      .get('password')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value :any) => {
        const element :UntypedFormControl = this.formData.get(
          'password',
        ) as UntypedFormControl;
        if (value) {
          element.setValidators([
            Validators.minLength(this.passMinLength),
            Validators.maxLength(this.passMaxLength),
            this.noWhitespaceValidator(),
          ]);
          element.updateValueAndValidity();
        } else if (!value) {
          element.clearValidators();
          element.updateValueAndValidity();
        }
      });
  }

  private onInputMeasureUpdated() :Observable<string> {
    return this.companyInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        this.companyService.search(name);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  // Create a form control for each object in Items array
  private createSettingsControls(settings :any[]) {
    // Initilize an empty form array
    const formArrayCtrls = this.fb.array([]);

    // Iterate each value
    if (settings && settings.length > 0) {
      settings.forEach((setting) => {
        // Create a new form control for each array value and push to form array
        formArrayCtrls.push(
          this.fb.group({
            businessAccountId: [
              setting.businessAccountId,
              Validators.compose([
                Validators.required,
                this.businessAccountValidator(),
              ]),
            ],
            primaryContact: [setting.primaryContact],
            receiveQuotationPdf: [setting.receiveQuotationPdf],
            receivePurchaseOrderPdf: [setting.receivePurchaseOrderPdf],
            receivePurchaseOrderInvoicePdf: [
              setting.receivePurchaseOrderInvoicePdf,
            ],
          }),
        );
      });
    }

    return formArrayCtrls;
  }
}
